/**
 * The bottom bar's More: a sheet over the current page listing the stars the
 * bar could not fit, then every area by group. One tap in, one tap out — the
 * page underneath is still where you were. Scoped inside an installed app.
 */
import React from 'react';

import { useLocation } from 'react-router-dom';

import { useAppStore } from '@/stores/useAppStore';
import { useWorkspaceNav } from '@features/workspace/hooks/useWorkspaceNav';
import { useInstalledApp } from '@shared/app-identity/useInstalledApp';
import { BottomSheet, DestinationRow } from '@shared/components/ui';
import { destinationForPath } from '@shared/navigation/destinations';
import { useRail } from '@shared/navigation/useRail';

import { moreSheetSections } from './moreSheetSections';
import { countForStar } from './starSlots';

interface MoreSheetProps {
  open: boolean;
  onClose: () => void;
}

export const MoreSheet: React.FC<MoreSheetProps> = ({ open, onClose }) => {
  const { pathname } = useLocation();
  const { ids } = useRail();
  const installedApp = useInstalledApp();
  const counts = useAppStore(s => s.navCounts);
  const { openDestination } = useWorkspaceNav();

  const { pinned, groups } = moreSheetSections(ids, installedApp);
  const activeId = destinationForPath(pathname)?.id;
  const open_ = (id: string) => {
    openDestination(id);
    onClose();
  };

  const sections = pinned.length > 0 ? [{ label: 'Pinned', items: pinned }, ...groups] : groups;

  return (
    <BottomSheet isOpen={open} onClose={onClose} title="More">
      {sections.map(section => (
        <section key={section.label} className="pb-2">
          <h3 className="text-muted-foreground px-4 pt-3 pb-1 text-xs font-medium tracking-wide uppercase">
            {section.label}
          </h3>
          <ul aria-label={section.label}>
            {section.items.map(d => (
              <DestinationRow
                key={d.id}
                d={d}
                active={activeId === d.id}
                count={countForStar(d.id, counts)}
                onOpen={open_}
              />
            ))}
          </ul>
        </section>
      ))}
    </BottomSheet>
  );
};
